import { api } from '../api.ts';
import { brut, h, instant, jour, peindre, type Brut } from '../ui.ts';
import { decouper, part } from '../anneau.ts';

/** Une case d'un axe : ce qu'on y compte, et le cran solide quand l'axe en porte un. */
export interface CaseAxe { cle: string; libelle: string; n: number; solide?: number }

/**
 * Un axe de répartition du vivier. Les cinq ont la même forme — c'est ce qui permet de
 * les dessiner une seule fois, en carte et en anneau, plutôt que cinq fois.
 */
export interface AxeRepartition {
  cle: string; titre: string; multiple: boolean; ordonne: boolean;
  acte: string; cases: CaseAxe[];
}

interface Repartition { profils: number; evalues: number; axes: AxeRepartition[] }

interface Usage {
  periode: string;
  /** La date à partir de laquelle tout l'écran compte — nulle depuis l'origine. */
  depuis: string | null;
  seuil_telechargements_mois: number;
  vivier: { total: number; a_qualifier: number };
  qualite: { solides: number; profils: number; part: number };
  utilisateurs: {
    // `id`, `prenom` et `nom` sont nuls pour un trigramme qui agit sans compte — `SITE`,
    // la voie publique. L'omettre du tableau faisait disparaître ses entrées des totaux.
    id: string | null; trigramme: string; prenom: string | null; nom: string | null;
    role: string; statut: string; vu_le: string | null;
    ajouts: number; qualifies: number; missions: number; rapprochements: number; dossiers: number;
    documents_ouverts: number; documents_telecharges: number; cv_wakea: number;
    telechargements_du_mois: number; seuil_depasse: boolean;
  }[];
  mensuel: { mois: string; ajouts: number }[];
  a_resynthetiser: number;
  repartition: Repartition;
  candidatures: {
    attente: number; a_lire: number;
    semaines: { semaine: string; recues: number; prises: number }[];
    refusees: { motif: string; n: number; dernier: string }[];
  };
}

const COLONNES = '214px 76px 88px 84px 104px 84px 104px 100px 96px 1fr';
const ENTETES = ['UTILISATEUR', 'AJOUTS', 'QUALIFIÉS', 'MISSIONS', 'RAPPROCH.', 'DOSSIERS',
                 'DOCS OUVERTS', 'TÉLÉCHARGÉS', 'CV WAKEA', 'DERNIER ACCÈS'];
const CHAMPS = ['ajouts', 'qualifies', 'missions', 'rapprochements', 'dossiers',
                'documents_ouverts', 'documents_telecharges', 'cv_wakea'] as const;

/** Ce que la période couvre, en toutes lettres — un titre de bloc ne doit rien laisser deviner. */
/**
 * Les trigrammes qui agissent sans compte et qu'on montre quand même.
 *
 * Le journal exige un trigramme : il n'accepte pas de ligne anonyme. `SITE` en fait partie,
 * et il a sa place ici — les candidatures venues du site sont des profils entrés au vivier,
 * donc du travail à compter.
 *
 * `PER` et `SYS` n'y sont pas, volontairement. `PER`, c'est la personne concernée exerçant
 * ses droits ; `SYS`, la création du tout premier compte. Ni l'un ni l'autre n'alimente une
 * seule colonne de ce tableau, et les y afficher donnait deux collaborateurs fantômes. Ils
 * restent au journal, qui est leur place : c'est là qu'on va chercher qui a fait quoi.
 */
const SANS_COMPTE: Record<string, { nom: string; quoi: string }> = {
  SITE: { nom: 'Candidatures venues du site', quoi: 'la voie publique, quand quelqu’un postule' },
};
/** Ce qui n'est ni un compte ni `SITE` n'a rien à faire dans un tableau d'activité. */
const estDuTravail = (x: { id: string | null; trigramme: string }) =>
  Boolean(x.id) || x.trigramme in SANS_COMPTE;

const LIBELLE_PERIODE: Record<string, string> = {
  mois: 'ce mois-ci', '3mois': 'sur 3 mois', '6mois': 'sur 6 mois',
  '12mois': 'sur 12 mois', origine: "depuis l'origine",
};

const PERIODES: [string, string][] = [['mois', 'Ce mois'], ['3mois', '3 mois'], ['6mois', '6 mois'],
                                      ['12mois', '12 mois'], ['origine', "Depuis l'origine"]];

/**
 * Une teinte par axe, déclinée en six pas du clair au foncé.
 *
 * Le cuivre du cabinet tient la chaîne de valeur ; les cinq autres teintes en sont les
 * voisines sourdes, choisies pour s'écarter entre elles d'au moins 8 unités OKLab — assez
 * pour qu'aucune paire ne se confonde, y compris en vision déficiente. À l'intérieur d'un
 * axe, la déclinaison suit la luminosité : les pas sont calculés en OKLCh, parce que c'est
 * le seul espace où « un pas de clarté » vaut la même chose quelle que soit la teinte.
 * Interpolées en RVB, les mêmes rampes s'écraseraient sur l'ocre et s'étaleraient sur le bleu.
 *
 * La couleur ne porte jamais l'information seule : chaque part est nommée à côté de sa barre.
 */
const TEINTES_AXE: Record<string, { base: string; rampe: string[] }> = {
  chaine:     { base: '#A06541', rampe: ['#E9C1AA', '#D4A183', '#BE815B', '#A06541', '#7C4F33', '#5A3A26'] },
  terrains:   { base: '#5F798D', rampe: ['#BCCDDA', '#9BB1C1', '#7A95A9', '#5F798D', '#4A5E6D', '#36444F'] },
  famille:    { base: '#5E8058', rampe: ['#BCD2B7', '#9AB794', '#799C72', '#5E8058', '#496444', '#364832'] },
  role:       { base: '#8F687B', rampe: ['#DDC2CE', '#C4A2B2', '#AC8397', '#8F687B', '#6F5160', '#513B45'] },
  profondeur: { base: '#877529', rampe: ['#D6CBA0', '#BCAD74', '#A39046', '#877529', '#695B22', '#4C421A'] },
  posture:    { base: '#A56050', rampe: ['#EDBEB2', '#D99C8E', '#C37B6A', '#A56050', '#804B3E', '#5D372E'] },
};
const TEINTE_DEFAUT = { base: '#6B6F75', rampe: ['#C9CBCE', '#AEB1B5', '#93979C', '#787D83', '#5D6268', '#42464B'] };

const MOIS = ['janv.', 'févr.', 'mars', 'avril', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'];

/** Pourquoi une candidature n'est pas entrée, en français, et ce qu'il y a à en faire. */
const MOTIFS_REFUS: Record<string, string> = {
  pot_de_miel: 'piège anti-robot déclenché',
  trop_rapide: 'formulaire rempli trop vite',
  champs_invalides: 'un champ refusé par MANA',
  cv_absent: 'aucun CV joint',
  cv_refuse: 'CV dans un format refusé',
  fichier_trop_lourd: 'CV de plus de 5 Mo',
  trop_de_champs: 'formulaire inattendu',
  corps_illisible: 'envoi interrompu',
  debit: 'débit dépassé depuis la même adresse',
};

/**
 * Une seule série : des barres, pas de légende — le titre nomme la mesure.
 *
 * Chaque barre porte son chiffre. Ne le mettre que sur la dernière obligeait à estimer les
 * autres à l'œil, sur une hauteur de soixante-dix pixels, pour une série qui tient en six
 * nombres : on dessinait un graphique là où un tableau aurait suffi. Avec les chiffres,
 * la forme dit la tendance et les chiffres disent les valeurs.
 *
 * La largeur des barres suit leur nombre : vingt-quatre mois à trente-deux pixels
 * déborderaient de la colonne.
 */
export function barres(mensuel: Usage['mensuel']): Brut {
  if (!mensuel.length) return h`<p class="sm sourd">Pas encore d'historique.</p>`;
  const haut = Math.max(...mensuel.map((m) => m.ajouts), 1);
  const dernier = mensuel.length - 1;
  // Les colonnes se partagent la largeur au lieu de la laisser vide à droite : six mois à
  // trente-deux pixels se tassaient dans un coin d'un bloc qui en fait mille. Le plafond
  // évite qu'une série de deux mois donne deux pavés.
  return h`
    <div class="histo" style="margin-top:12px">
      ${mensuel.map((m, i) => h`
        <div class="histo-col">
          <span class="pt" style="color:${i === dernier ? 'var(--copper)' : 'var(--basalt)'};
            font-weight:${i === dernier ? 'bold' : 'normal'}">${m.ajouts}</span>
          <div class="histo-barre" title="${m.mois} · ${m.ajouts} profil${m.ajouts > 1 ? 's' : ''}"
            style="height:${Math.max(m.ajouts ? 3 : 1, Math.round((m.ajouts / haut) * 70))}px;
            background:${i === dernier ? 'var(--copper)' : m.ajouts ? 'var(--obsidian)' : 'var(--line)'}"></div>
          <span class="pt sourd">${MOIS[Number(m.mois.slice(5, 7)) - 1]}</span></div>`)}
    </div>`;
}

/**
 * Les candidatures venues du site. Deux chiffres, et pas un de plus : ce qui est arrivé,
 * et ce que personne n'a encore pris. Le second est le seul qui demande une action — un
 * dossier qui dort est à la fois un gâchis et un défaut de conformité, puisque la
 * personne a été informée que nous allions regarder.
 */
function entrees(c: Usage['candidatures']): Brut {
  const recues = c.semaines.reduce((n, s) => n + s.recues, 0);
  if (!recues && !c.attente && !c.a_lire) return h``;
  const derniere = c.semaines[c.semaines.length - 1];
  const refus = c.refusees.reduce((n, r) => n + r.n, 0);
  return h`
    <div class="carte" style="margin-top:16px">
      <div class="entre"><div><div class="sur">CANDIDATURES VENUES DU SITE</div>
        <p class="sm sourd" style="margin:6px 0 0">${recues} sur les huit dernières semaines,
          dont ${derniere?.recues ?? 0} cette semaine.</p></div>
        <div class="rang" style="gap:8px">
          ${c.a_lire ? h`<button class="bt" data-act="voir-a-lire">${c.a_lire} CV à lire</button>` : ''}
          ${c.attente
            ? h`<button class="bt p" data-act="voir-entrees">${c.attente} à prendre en main</button>`
            : h`<span class="et v">Tout a été pris en main</span>`}</div></div>
      ${refus ? h`<div class="enc ar" style="margin-top:12px">
        <span class="sur">${refus} CANDIDATURE${refus > 1 ? 'S' : ''} PRÉSENTÉE${refus > 1 ? 'S' : ''} ET REFUSÉE${refus > 1 ? 'S' : ''}</span>
        <p>Quelqu'un a cru postuler et n'est pas entré au vivier. Les motifs sont au journal,
           sans aucune donnée sur ces personnes.</p>
        <div class="rang" style="flex-wrap:wrap;gap:7px;margin-top:9px">
          ${c.refusees.map((r) => h`<span class="et o">${r.n} ·
            ${MOTIFS_REFUS[r.motif] ?? r.motif}</span>`)}</div></div>` : ''}
    </div>`;
}

/**
 * La répartition d'un axe : un anneau, et sous lui les parts en barres nommées.
 *
 * Les cases en rectangles qui doublaient cet anneau ont disparu : elles disaient la même
 * chose en moins lisible, et il fallait lire deux fois le même écran pour comparer deux
 * axes. La barre donne la grandeur d'un coup d'œil, l'anneau donne la part, le libellé
 * donne l'identité — chacun une fois.
 *
 * Chaque ligne est cliquable : la répartition dit *où*, le vivier dit *qui*.
 */
export function panneau(axe: AxeRepartition): Brut {
  const teinte = TEINTES_AXE[axe.cle] ?? TEINTE_DEFAUT;
  const titre = h`<div class="rang" style="gap:8px;align-items:center;min-width:0">
    <span style="width:9px;height:9px;flex:0 0 9px;border-radius:2px;background:${teinte.base}"></span>
    <span class="pt sourd titre-axe">${axe.titre}</span></div>`;
  // Le tri — ou son absence sur un axe ordonné — appartient à `decouper`, et à lui seul.
  const pleines = axe.cases.filter((x) => x.n > 0);
  // Les cases que personne ne tient restent visibles, en sourdine et après les autres :
  // un trou dans le vivier est un renseignement, et le masquer le ferait oublier.
  const creuses = axe.cases.filter((x) => x.n === 0);

  const { parts, total } = decouper(pleines, teinte.rampe.length, axe.ordonne);
  const segments = parts.map((x, i) => {
    const rang = axe.ordonne ? parts.length - 1 - i : i;
    return { ...x, teinte: x.detail ? TEINTE_DEFAUT.base : teinte.rampe[rang] ?? teinte.base };
  });

  /**
   * L'anneau et la liste ne portent pas la même chose, et c'est voulu.
   *
   * L'anneau se limite à six parts : au-delà, les segments deviennent des traits et la roue
   * ne se lit plus. La liste, elle, montre **toutes** les cases — une grille de rôles se lit
   * en entier, et une case à zéro est précisément ce qu'il reste à aller chercher. Les cases
   * que l'anneau a regroupées prennent la teinte de sa part « Autres » : on retrouve ainsi
   * dans la liste ce que le gris du camembert rassemblait.
   */
  const teintes = new Map(segments.filter((s) => s.cle).map((s) => [s.cle, s.teinte]));
  const toutes = axe.ordonne
    // Une séquence se lit dans son ordre, les trous compris : rejeter les cases vides en
    // fin de liste faisait lire « 2, 3, 4 » puis « 1, 5 », et l'échelle n'existait plus.
    ? axe.cases
    : [...pleines].sort((a, b) => b.n - a.n || a.libelle.localeCompare(b.libelle)).concat(creuses);

  const haut = Math.max(...toutes.map((x) => x.n), 1);

  const C = 60, R = 52, r = 33;
  const direPart = (x: { libelle: string; n: number; detail?: string }) =>
    `${x.libelle} — ${x.n} profil${x.n > 1 ? 's' : ''}`
    + (total ? ` · ${Math.round((x.n / total) * 100)} %` : '')
    + (x.detail ? ` — ${x.detail}` : '');

  // Une part unique ne se trace pas en arc : un arc de trois cent soixante degrés revient
  // à son point de départ, et le chemin se referme sur rien. Deux cercles concentriques
  // en pair/impair donnent le même anneau, plein.
  const disque = h`<path d="M60 8 A52 52 0 1 1 59.99 8 Z M60 27 A33 33 0 1 0 59.99 27 Z"
    fill="${segments[0]!.teinte}" fill-rule="evenodd"><title>${direPart(segments[0]!)}</title></path>`;

  const ligne = (x: CaseAxe) => {
    const vide = x.n === 0;
    return h`
      <button class="barre ${vide ? 'creuse' : ''}" data-act="${axe.acte}" data-valeur="${x.cle}"
        title="${vide ? `${x.libelle} — aucun profil` : direPart(x)} — ouvrir la liste">
        <span class="barre-nom">${x.libelle}</span>
        <span class="barre-jauge">${vide ? '' : h`<i style="width:${Math.max(3, Math.round((x.n / haut) * 100))}%;
          background:${teintes.get(x.cle) ?? TEINTE_DEFAUT.base}"></i>`}</span>
        <span class="barre-n">${x.n}</span></button>`;
  };

  return h`
    <div class="panneau">
      <div class="entre" style="align-items:baseline">
        ${titre}
        <span class="pt sourd">${total} ${axe.multiple ? 'positions' : 'profils'}</span>
      </div>
      <div class="rang" style="align-items:center;gap:18px;margin-top:10px">
        <svg viewBox="0 0 120 120" width="104" height="104" style="flex:0 0 104px" role="img"
             aria-label="${axe.titre} — répartition de ${total} ${axe.multiple ? 'positions' : 'profils'}">
          ${segments.length === 1 ? disque
            : segments.map((s) => h`<path d="${part(C, C, R, r, s.a0, s.a1)}" fill="${s.teinte}"
                stroke="#FCFCFB" stroke-width="1.5"><title>${direPart(s)}</title></path>`)}
          <text x="60" y="58" text-anchor="middle" font-size="20" font-weight="bold"
                fill="#0B0D10">${total}</text>
          <text x="60" y="71" text-anchor="middle" font-size="7" letter-spacing="0.9"
                fill="#6B6F75">${axe.multiple ? 'POSITIONS' : 'PROFILS'}</text>
        </svg>
        <div class="pousse liste-parts ${toutes.length > 8 ? 'deborde' : ''}"
             style="min-width:0">${toutes.map(ligne)}</div>
      </div>
      ${axe.multiple ? h`<p class="pt sourd" style="margin:8px 0 0">
        Un profil peut tenir plusieurs cases.</p>` : ''}
    </div>`;
}

export async function ecranAccueil(periode: string, chrome: (corps: Brut) => Brut): Promise<void> {
  const u = await api.get<Usage>(`/api/usage?periode=${encodeURIComponent(periode)}`);
  const total = (champ: typeof CHAMPS[number]) =>
    u.utilisateurs.filter(estDuTravail).reduce((n, x) => n + x[champ], 0);
  // Le tableau ne montre que ce qui est du travail de quelqu'un.
  const lignes = u.utilisateurs.filter(estDuTravail);
  const depassements = lignes.filter((x) => x.seuil_depasse);

  peindre('#app', chrome(h`
    <div class="page">
      <div class="entre"><div><h1 class="h1">Usage de MANA</h1>
        <p class="sous" style="margin:4px 0 0">Tout l'écran est compté
          <b>${LIBELLE_PERIODE[u.periode] ?? ''}${u.depuis ? ` — depuis le ${jour(u.depuis)}` : ''}</b> ·
          ${lignes.length} personne${lignes.length > 1 ? 's' : ''} ·
          ${u.vivier.total} profils au vivier, dont ${u.vivier.a_qualifier} à qualifier ·
          vue réservée aux administrateurs</p></div>
        <div class="rang">${PERIODES.map(([v, l]) => h`<button class="puce"
          aria-pressed="${u.periode === v}" data-act="periode" data-valeur="${v}">${l}</button>`)}</div></div>

      <div class="tuiles">
        <div class="tuile k"><b>${total('ajouts')}</b><span>profils ajoutés</span></div>
        <div class="tuile"><b>${total('qualifies')}</b><span>profils qualifiés</span></div>
        <div class="tuile"><b>${total('missions')}</b><span>missions ouvertes</span></div>
        <div class="tuile"><b>${total('rapprochements')}</b><span>rapprochements</span></div>
        <div class="tuile"><b>${total('dossiers')}</b>
          <span>dossiers de présentation · <span style="color:var(--copper)">${total('cv_wakea')} CV WAKEA</span></span></div>
        <div class="tuile q" title="Profils tenant au moins une case de la cartographie au cran « solide » ou mieux">
          <b>${u.qualite.part} %</b>
          <span>indice de qualité · ${u.qualite.solides} profil${u.qualite.solides > 1 ? 's' : ''}
            solide${u.qualite.solides > 1 ? 's' : ''} sur ${u.qualite.profils}</span></div>
        <div class="tuile"><b>${total('documents_ouverts')}</b>
          <span>documents ouverts · <span style="color:var(--rouge)">${total('documents_telecharges')} téléchargés</span></span></div>
      </div>


      ${entrees(u.candidatures)}

      ${u.a_resynthetiser ? h`<div class="carte" style="margin-top:16px">
        <div class="entre"><div><div class="sur">SYNTHÈSES À REFAIRE</div>
          <p class="sm sourd" style="margin:6px 0 0">${u.a_resynthetiser} dossier${u.a_resynthetiser > 1 ? 's portent' : ' porte'}
            une synthèse produite par une consigne antérieure : ni positionnement sectoriel, ni niveau d'IA perçu.</p></div>
          <button class="bt p" data-act="voir-resynthese">Les ouvrir</button></div>
      </div>` : ''}

      <section style="display:flex;flex-direction:column;gap:9px">
        <div class="rang"><span class="sur">ACTIVITÉ PAR UTILISATEUR</span></div>
        <div class="large"><div>
          <div class="th" style="grid-template-columns:${COLONNES}">${ENTETES.map((t) => h`<span>${t}</span>`)}</div>
          ${lignes.map((x) => h`<div class="tr" style="grid-template-columns:${COLONNES}">
            <div class="rang" style="gap:9px"><span class="tri">${x.trigramme}</span>
              <div><div style="font-size:12.5px;color:var(--obsidian)">${
                x.prenom ? `${x.prenom} ${x.nom}` : SANS_COMPTE[x.trigramme]?.nom ?? 'Sans compte'}</div>
                <div class="pt sourd">${x.id ? `${x.role}${x.statut === 'suspendu' ? ' · suspendu' : ''}`
                  : SANS_COMPTE[x.trigramme]?.quoi ?? 'pas un compte'}</div></div></div>
            ${CHAMPS.slice(0, 5).map((c) => h`<span style="font-size:12.5px;color:var(--obsidian)">${x[c]}</span>`)}
            <span style="font-size:12.5px;color:${x.seuil_depasse ? 'var(--rouge)' : 'var(--obsidian)'}">${x.documents_ouverts}</span>
            <span style="font-size:12.5px;color:${x.seuil_depasse ? 'var(--rouge)' : 'var(--obsidian)'}">${x.documents_telecharges}</span>
            <span style="font-size:12.5px;color:${x.seuil_depasse ? 'var(--rouge)' : 'var(--obsidian)'}">${x.cv_wakea}</span>
            <span class="sm sourd">${x.vu_le ? instant(x.vu_le) : 'jamais'}</span></div>`)}
          <div class="tr tot" style="grid-template-columns:${COLONNES}">
            <span class="pt sourd" style="letter-spacing:.8px">TOTAL</span>
            ${CHAMPS.map((c) => h`<span style="font-size:12.5px;font-weight:bold;color:var(--obsidian)">${total(c)}</span>`)}
            <span class="sm sourd">—</span></div>
        </div></div>

        ${depassements.length ? h`<div class="enc ar" style="flex-direction:row;align-items:center;gap:14px;flex-wrap:wrap">
          <div class="pousse"><span class="sur">SEUIL DE SORTIE DE DOCUMENTS DÉPASSÉ</span>
            <p style="margin-top:3px;color:var(--obsidian)"><b>${brut(depassements.map((x) =>
              `${x.trigramme} a sorti ${x.telechargements_du_mois} documents ce mois`).join(' · '))}.</b>
              Le seuil est fixé à ${u.seuil_telechargements_mois} par mois et par utilisateur.</p></div>
          <button class="bt s" data-act="aller" data-vue="console">Régler le seuil</button></div>` : ''}
      </section>

      <div style="margin-top:4px">
        <div class="filet-l"><span class="sur">PROFILS AJOUTÉS PAR MOIS</span>
          <span class="pt sourd pousse" style="text-align:right">${LIBELLE_PERIODE[u.periode] ?? ''}</span></div>
        ${barres(u.mensuel)}</div>

      <section style="margin-top:24px">
        <div class="filet-l"><span class="sur">RÉPARTITION DU VIVIER</span>
          <span class="pt sourd pousse" style="text-align:right">${u.repartition.evalues}
            dossier${u.repartition.evalues > 1 ? 's' : ''} cartographié${u.repartition.evalues > 1 ? 's' : ''}
            sur ${u.repartition.profils} · ${LIBELLE_PERIODE[u.periode] ?? ''}</span></div>
        ${u.repartition.evalues || u.repartition.profils
          ? h`<div class="panneaux">${u.repartition.axes.map(panneau)}</div>`
          : h`<p class="sm" style="margin:12px 0 0;line-height:1.55">Aucun dossier n'est encore
              cartographié. La cartographie s'écrit à l'enregistrement d'une synthèse — les
              ${u.a_resynthetiser || u.repartition.profils} dossier${(u.a_resynthetiser || u.repartition.profils) > 1 ? 's' : ''}
              de la file <b>Synthèse à refaire</b> la produiront, un par un.</p>`}
      </section>
    </div>`));
}
